import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Seo from "../components/Seo";
import { IMAGES, SITE } from "../siteConfig";
import { Lifecycle } from "../components/Lifecycle";
import { Reveal } from "../components/Reveal";

const CONFIG = {
  "mangalam-enterprises": {
    name: "Mangalam Enterprises",
    sub: "Industrial & Commercial Vehicle Tyre Solutions",
    image: "https://customer-assets-wrfwihn1.emergentagent.net/job_industrial-tyre-hub/artifacts/o8hqn80p_image.png",
    seo: "Mangalam Enterprises — industrial tyres, mining tyres and BKT commercial vehicle radial (CVR) tyres for trucks, buses and fleets across Jharkhand.",
    about: [
      "Established in 2011, Mangalam Enterprises is the founding company of Mangalam Group — built around industrial tyres, mining tyres and commercial vehicle radial tyres.",
      "As a BKT tyre distribution partner, the company serves dealers, fleet operators and industrial customers with a portfolio that spans industrial fitments through to the latest CVR range, added in 2025.",
    ],
    blocks: [
      { title: "Tippers", copy: "Steer Grip, XL Grip, XL Grip Ultra, Rock Grip and SR 425 radial for heavy tipper duty.", link: "/products?segment=manglam-enterprises&category=Tipper" },
      { title: "Backhoe Loaders", copy: "Loader Plus, AT 621, AT 625, EM 937, XL Grip and BK 6060 for backhoe fitments.", link: "/products?segment=manglam-enterprises&category=Backhoe%20Loader" },
      { title: "Wheel Loaders", copy: "XL Grip, Loader Plus, Rock Grip, EM 937 and the SR radial series across all popular loader sizes.", link: "/products?segment=manglam-enterprises&category=Wheel%20Loader" },
      { title: "Graders", copy: "Super Grader, XL Grip, SR 23 and SR 25 Plus for motor grader applications.", link: "/products?segment=manglam-enterprises&category=Grader" },
      { title: "High Speed Cranes", copy: "SR 423 and AM 27 radial tyres for high-speed mobile crane operations.", link: "/products?segment=manglam-enterprises&category=High%20Speed%20Crane" },
      { title: "Rough Terrain Cranes", copy: "Crane Special for rough terrain crane fitments.", link: "/products?segment=manglam-enterprises&category=Rough%20Terrain%20Crane" },
      { title: "Pick & Carry Cranes", copy: "RM 2002, XL Grip, Super Grader and Loader Plus for pick-and-carry crane operations.", link: "/products?segment=manglam-enterprises&category=Pick%20%26%20Carry%20Crane" },
      { title: "Skid Steers", copy: "Power Trax HD and Skid Power HD for skid steer loaders in tough site work.", link: "/products?segment=manglam-enterprises&category=Skid%20Steer" },
      { title: "Soil Compactors", copy: "TR 387 and TR 390 for soil compactor applications.", link: "/products?segment=manglam-enterprises&category=Soil%20Compactor" },
      { title: "Pneumatic Compactors", copy: "Pac Master and Pac Master Plus for pneumatic compactor fleets.", link: "/products?segment=manglam-enterprises&category=Pneumatic%20Compactor" },
      { title: "Port Applications", copy: "Container King, EM 937, Rock Grip, SM 55, Port King Classic and Con Tailer Port for container handlers, reach stackers, RTG cranes and port trailers.", link: "/products?segment=manglam-enterprises&category=Port%20Application" },
      { title: "Forklifts", copy: "Maglift STD, Maglift Eco, Power Trax HD and LM 81 across the full forklift size range.", link: "/products?segment=manglam-enterprises&category=Forklift" },
      { title: "Tow Tractors", copy: "LM 63 for baggage and cargo tow tractor fitments.", link: "/products?segment=manglam-enterprises&category=Tow%20Tractor" },
      { title: "Commercial Vehicle Radial", copy: "BKT CVR range for trucks and buses — Loadexpert and Milexpert in 11.00R20 and 295/90R20.", link: "/products?segment=manglam-enterprises&tyre_type=CVR" },
    ],
    cta: { label: "BROWSE THE RANGE", to: "/products?segment=manglam-enterprises" },
  },
  "apex-infra": {
    name: "Apex Infra",
    sub: "Earthmoving & Off-Highway Tyre Solutions",
    image: IMAGES.loader,
    seo: "Apex Infra — the complete BKT India OTR lineup for loaders, graders, dump trucks, underground mining and heavy earthmoving equipment in Jharkhand.",
    about: [
      "Apex Infra was added to the group in 2022 to deepen its earthmoving and off-highway tyre business — the machines that build roads, move overburden and keep sites running.",
      "The catalogue carries the complete BKT India OTR pattern lineup — radial, bias and solid options with sizes and technical data mapped to each machine and application.",
    ],
    blocks: [
      { title: "Loaders", copy: "L3/L5 radial and bias options for wheel loaders in stockpile and face work." },
      { title: "Excavators", copy: "Tyres for wheeled excavators and support equipment." },
      { title: "Graders", copy: "Grader tyres for haul-road maintenance and construction." },
      { title: "Dumpers", copy: "Radial OTR tyres for rigid dump trucks in open-cast mining." },
      { title: "Tippers", copy: "Heavy-duty fitments for on/off-road tipper operations." },
      { title: "Construction Equipment", copy: "Tyres for the wider construction fleet — compactors, cranes and more." },
      { title: "Mining Equipment", copy: "Severe-service tyres matched to mining duty cycles." },
    ],
    cta: { label: "FIND YOUR TYRE", to: "/products?segment=apex-infra" },
  },
  amr: {
    name: "Apex Mineral Resources",
    short: "AMR",
    sub: "Tyre Management. Mining Intelligence. Better Performance.",
    image: IMAGES.engineer,
    seo: "Apex Mineral Resources (AMR) — technology-driven tyre management, lifecycle tracking, inspection and cost-per-hour optimisation for mining operations.",
    about: [
      "AMR is the technology, service and mining-solutions arm of Mangalam Group. It exists to answer one question: what is every tyre in the fleet actually doing?",
      "Through structured tyre management — fitment records, inspections, repair and cut tracking, running hours and cost-per-hour analysis — AMR turns tyre data into maintenance plans and purchasing decisions.",
    ],
    capabilities: [
      "Tyre life improvement", "Fleet visibility", "Maintenance planning", "Tyre utilisation",
      "Repair & cut tracking", "Cost-per-hour monitoring", "Operational efficiency", "Data-driven decisions",
    ],
    cta: { label: "EXPLORE TYRE MANAGEMENT", to: "/contact" },
  },
};

export default function Business() {
  const { slug } = useParams();
  const b = CONFIG[slug];
  if (!b) return <Navigate to="/" replace />;

  const businessUrl = new URL(`/businesses/${slug}`, SITE.url).toString();
  const businessSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: b.name,
    description: b.seo,
    url: businessUrl,
    image: b.image,
    parentOrganization: { "@type": "Organization", name: SITE.name, url: SITE.url },
    areaServed: ["Jharkhand", "Eastern India"],
  };

  return (
    <div data-testid={`business-page-${slug}`} className="pt-[72px]">
      <Seo title={`${b.name} — Mangalam Group`} description={b.seo} path={`/businesses/${slug}`} jsonLd={businessSchema} />

      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          <img src={b.image} alt={`${b.name} — ${b.sub}`} className={`h-full w-full object-cover img-industrial ${slug === "mangalam-enterprises" ? "opacity-60" : "opacity-30"}`} />
          <div className={`absolute inset-0 ${slug === "mangalam-enterprises" ? "bg-gradient-to-b from-[#F7F8F4] via-[#F7F8F4]/88 to-[#F7F8F4]/55 lg:bg-gradient-to-r lg:from-[#F7F8F4] lg:via-[#F7F8F4]/88 lg:to-[#F7F8F4]/25" : "bg-gradient-to-b from-obsidian/60 to-obsidian"}`} />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <Reveal>
            <p className="font-mono text-sm sm:text-base font-bold uppercase tracking-[0.26em] text-ember">Mangalam Group</p>
            <h1 className="mt-5 font-condensed text-4xl sm:text-5xl lg:text-6xl font-bold uppercase leading-[1.0] tracking-tight text-white">
              {b.name}
            </h1>
            <p className="mt-4 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-300">{b.sub}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-[1.1fr_1fr]">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-ember">About</p>
            <div className="mt-5 space-y-5">
              {b.about.map((p, i) => (
                <p key={i} className="text-base sm:text-lg leading-relaxed text-slate-300">{p}</p>
              ))}
            </div>
            {b.capabilities && (
              <ul className="mt-8 grid gap-2 sm:grid-cols-2">
                {b.capabilities.map((c) => (
                  <li key={c} className="flex items-center gap-3 border border-white/10 bg-obsidian-2 px-4 py-3 text-sm text-slate-300">
                    <Check className="h-4 w-4 shrink-0 text-ember" /> {c}
                  </li>
                ))}
              </ul>
            )}
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full min-h-[280px] overflow-hidden border border-white/10">
              <img src={b.image} alt={`${b.name} operations`} className="h-full w-full object-cover img-industrial" />
            </div>
          </Reveal>
        </div>
      </section>

      {b.blocks && (
        <section className="border-t border-white/10 bg-obsidian-2 py-20 lg:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <p className="font-mono text-sm sm:text-base font-bold uppercase tracking-[0.26em] text-ember">
                {slug === "apex-infra" ? "Applications" : "What We Do"}
              </p>
            </Reveal>
            <div className="mt-10 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
              {b.blocks.map((blk, i) => (
                <Reveal key={blk.title} delay={(i % 3) * 0.07} className="bg-obsidian-2">
                  {blk.link ? (
                    <Link
                      to={blk.link}
                      data-testid={`business-block-${blk.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="group block h-full p-7 transition-colors hover:bg-obsidian-3"
                    >
                      <h3 className="font-condensed text-xl font-bold uppercase tracking-wide text-white transition-colors group-hover:text-ember">{blk.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate-400">{blk.copy}</p>
                      <p className="mt-4 inline-flex items-center gap-1.5 font-mono text-[9px] tracking-[0.22em] uppercase text-ember">
                        View range <ArrowRight className="h-3 w-3" />
                      </p>
                    </Link>
                  ) : (
                    <div className="h-full p-7">
                      <h3 className="font-condensed text-xl font-bold uppercase tracking-wide text-white">{blk.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate-400">{blk.copy}</p>
                    </div>
                  )}
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {slug === "amr" && (
        <section className="border-t border-white/10 bg-obsidian-2 py-20 lg:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-ember">The Tyre Lifecycle</p>
              <h2 className="mt-4 font-condensed text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white">
                Every Tyre, Tracked End to End
              </h2>
            </Reveal>
            <div className="mt-10"><Lifecycle /></div>
          </div>
        </section>
      )}

      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-wrap items-center justify-between gap-6 border border-white/10 bg-obsidian-2 p-8 lg:p-12">
            <h2 className="font-condensed text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white">
              Talk to the {b.short || b.name} team
            </h2>
            <div className="flex flex-wrap gap-3">
              <Link
                to={b.cta.to}
                data-testid="business-cta"
                className="inline-flex items-center gap-2 bg-ember px-7 py-4 font-mono text-[11px] tracking-[0.22em] text-[#10141B] transition-colors hover:bg-ember-hover"
              >
                {b.cta.label} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="business-contact-cta"
                className="inline-flex items-center gap-2 border border-white/20 px-7 py-4 font-mono text-[11px] tracking-[0.22em] text-white transition-colors hover:border-ember hover:text-ember"
              >
                CONTACT US
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
